import React, { createContext, useContext, useState, useEffect } from 'react';
import { flushSync } from 'react-dom';

// Track the last pointer position so the theme ripple radiates from the click.
let lastPointer = { x: null, y: null };
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', (e) => {
    lastPointer = { x: e.clientX, y: e.clientY };
  }, true);
}

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    
    if (savedTheme) {
      setTheme(savedTheme);
    } else {
      // Auto-detect system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialTheme = prefersDark ? 'dark' : 'light';
      setTheme(initialTheme);
      localStorage.setItem('theme', initialTheme);
    }
    
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      // Apply theme to document
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);
    }
  }, [theme, isInitialized]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    const apply = () => {
      // Set the attribute synchronously so the View Transition captures it.
      document.documentElement.setAttribute('data-theme', next);
      setTheme(next);
    };

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!document.startViewTransition || reduceMotion) {
      apply();
      return;
    }

    const x = lastPointer.x != null ? `${lastPointer.x}px` : '100%';
    const y = lastPointer.y != null ? `${lastPointer.y}px` : '0px';
    document.documentElement.style.setProperty('--vt-x', x);
    document.documentElement.style.setProperty('--vt-y', y);

    const transition = document.startViewTransition(() => {
      flushSync(apply);
    });
    // An interrupted ripple (rapid toggles, tab switch mid-animation) rejects
    // `finished`; the theme is already applied, so it's safe to ignore.
    transition.finished.catch(() => {});
  };

  const value = {
    theme,
    toggleTheme,
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
